import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * ProcessingStatus — the ONE "we're working on it" signal, on every page
 * (Apal 2026-11-02: "it should be absolutely clear to the user that right now
 * you're processing … sleek … the same way on every single page … people
 * saying what is happening, do I need to click something?" — "showing the
 * seconds, because this will help me if it's taking longer").
 *
 * A slim strip under the filters:
 * - says WHAT it is getting (the filters in force), not just "Loading";
 * - counts the seconds from the click (0s, 1s, 2s …);
 * - from 15s says it is still working and nothing needs clicking;
 * - pins to the top of the window once its spot scrolls away (the page
 *   scrolls the WINDOW and <main> is overflow-auto, so CSS sticky can't).
 * The caller dims and locks the old numbers underneath with dimClass().
 */

export function useElapsed(active) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!active) { setSecs(0); return undefined; }
    const start = Date.now();
    setSecs(0);
    const id = setInterval(() => setSecs(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [active]);
  return secs;
}

/** The old numbers under a running update: faded and not clickable. */
export const dimClass = (active) =>
  `transition-opacity duration-200 ${active ? 'opacity-40 pointer-events-none select-none' : ''}`;

export default function ProcessingStatus({ active, title = 'Updating results', what, className = 'mb-4', testId = 'processing-status' }) {
  const secs = useElapsed(active);
  const ref = useRef(null);
  const [pin, setPin] = useState(null);   // {left, width} while pinned to the window top
  useEffect(() => {
    if (!active) { setPin(null); return undefined; }
    const check = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // a laid-out strip whose spot has scrolled above the window's top edge
      setPin(r.height > 0 && r.top < 8 ? { left: r.left, width: r.width } : null);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => { window.removeEventListener('scroll', check); window.removeEventListener('resize', check); };
  }, [active]);
  if (!active) return null;

  const strip = (extra, style, id) => (
    <div role="status" aria-live="polite" data-testid={id} style={style}
      className={`overflow-hidden flex flex-wrap items-center gap-x-2.5 gap-y-1 px-4 py-2.5 rounded-lg bg-dark-800 border border-primary-600/40 shadow-lg shadow-black/40 text-sm ${extra}`}>
      <RefreshCw className="w-4 h-4 text-primary-400 animate-spin shrink-0" aria-hidden />
      <span className="font-semibold text-white whitespace-nowrap">{title}</span>
      {what && <span className="text-dark-300 whitespace-nowrap">· {what}</span>}
      <span className="text-dark-400 tabular-nums whitespace-nowrap" data-testid={id ? 'processing-seconds' : undefined}>· {secs}s</span>
      {secs >= 15 && (
        <span className="text-amber-300/90 whitespace-nowrap">· still working — a large recalculation, no need to click again</span>
      )}
      {/* the sweep: motion that says "working" even before the seconds tick */}
      <div className="absolute left-0 right-0 bottom-0 h-[2px] overflow-hidden" aria-hidden>
        <div className="updating-bar-sweep absolute inset-y-0 bg-primary-400 rounded-full" />
      </div>
    </div>
  );
  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* in place — kept (invisible) while pinned so nothing below jumps */}
      <div className={pin ? 'invisible' : ''}>{strip('relative', undefined, pin ? undefined : testId)}</div>
      {pin && strip('fixed z-50', { top: 8, left: pin.left, width: pin.width }, testId)}
    </div>
  );
}
